// Shared auth-contract constants — single source of truth so the hub, the main app, and every
// spoke reference identical names. Drift here = silent auth breakage (the cookie-name collision
// that broke local sessions was exactly this class of bug).

// The thin-session cookie. Deliberately DISTINCT from the legacy next-auth cookie (`civitai-token`, still
// set by the main app's libs/auth.ts) so the hub (thin ES256) and a still-live next-auth never share — and
// stomp — the same cookie during cutover. Existing next-auth sessions simply re-login at the hub.
export const SESSION_COOKIE_BASE = 'civ-token';
// Per-browser device id cookie (account-switch device set, section E). Same secure-prefix rules as the
// session cookie — derive its name via `deviceCookieName(secure)`, never hardcode the prefixed literal.
export const DEVICE_COOKIE_BASE = 'civ-device';
// Legacy NextAuth session cookie — READ-ONLY during the cutover (decoded by the jose helper). Same secure-prefix
// rules as the others, so resolve its name via `legacySessionCookieName(secure)`, never hardcode the literal
// (prod is `__Secure-civitai-token`, not `civitai-token`).
export const LEGACY_SESSION_COOKIE_BASE = 'civitai-token';
export const SECURE_COOKIE_PREFIX = '__Secure-';
export const HOST_COOKIE_PREFIX = '__Host-';

// Which slot this app writes its session and device cookies to, read per app from AUTH_SESSION_COOKIE_MODE
// (`sessionCookieMode()`). `shared` is today's `__Secure-` cookie on the registrable domain; `host` is a
// `__Host-` cookie that stays on the host that set it. Readers accept both slots during the move (cookies.ts).
export const SESSION_COOKIE_MODES = ['shared', 'host'] as const;
export type SessionCookieMode = (typeof SESSION_COOKIE_MODES)[number];

// Cross-domain account sync query param — the single marker the hub re-attaches after login and the
// destination's useDomainSync reads to kick off the auth-code flow (/api/auth/authorize).
export const SYNC_PARAM = 'sync-account';

// The SPOKE-side landing every first-party login is sent to: the hub's `/login?returnUrl=` points here, and
// this endpoint then starts the authorization-code flow back at the hub. Sibling of `SPOKE_CALLBACK_PATH`
// (first-party.ts), which is the other end of the same round trip.
//
// Shared because BOTH sides now depend on the exact string: the main app WRITES it (`buildHubLoginUrl`), and
// the hub MATCHES on it to decide whether a login is a cross-domain spoke hand-off (`establishSession`). If
// those drift, the hub silently stops handing off and falls back to writing its own `.civitai.com` session —
// which is the cross-domain account-switch bug, returning with no error and no failing test. Lives here
// rather than beside SPOKE_CALLBACK_PATH so it is reachable from `@civitai/auth/client` too (client.ts
// re-exports this file; it does not re-export first-party.ts).
export const SPOKE_AUTHORIZE_PATH = '/api/auth/authorize';

// The registrable domains (eTLD+1) the Civitai family owns. The single source of truth for "is this one of
// our hosts" — the post-login redirect guard (isCivitaiOrigin) and any other family-host check reference THIS
// list so they can't drift. `civitaic.com` is the auto-deploy PREVIEW domain (ephemeral per-PR hosts like
// pr-2468.civitaic.com) — it MUST be here or the unified login funnel drops the spoke's returnUrl on previews.
// NB: this is the registrable-domain OWNERSHIP list (a static, DB-free security guard); the OAuth
// `TrustedSpokeDomain` table is the SEPARATE per-host AUTHORIZATION registry (async, DB-backed, finer-grained).
// Both must know about every family domain, but they answer different questions — see isCivitaiOrigin.
export const CIVITAI_OWNED_DOMAINS = ['civitai.com', 'civitai.red', 'civitaic.com'] as const;

// Credentials-provider id the cross-root receiver registers; the client signIn() id must match.
export const ACCOUNT_SWITCH_PROVIDER_ID = 'account-switch';

// Signals to the client that its session cookie should be refreshed (mirrors the main app's
// shared/constants/auth.constants.ts).
export const SESSION_REFRESH_HEADER = 'x-session-refresh';
export const SESSION_REFRESH_COOKIE = 'civ-session-refresh';

// NB: redis session key names (TOKEN_STATE / USER_TOKENS / ALL) are NOT declared here — they're owned by
// @civitai/redis and injected into createSessionRegistry, so @civitai/auth takes no redis dependency.
